/**
 * Client-Side Message Queue
 * Handles message queuing during WebSocket disconnections
 * Implements auto-retry with exponential backoff
 */

class MessageQueue {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.maxRetries = 3;
    this.retryDelay = 1000; // Start with 1 second
    this.maxQueueSize = 100;
    
    // Load from localStorage on init
    this.loadFromStorage();
  }

  /**
   * Add message to queue
   */
  enqueue(message) {
    // Add metadata
    const queuedMessage = {
      ...message,
      id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      queuedAt: Date.now(),
      retries: 0,
      status: 'pending' // pending, sending, sent, failed
    };

    // Check queue size
    if (this.queue.length >= this.maxQueueSize) {
      console.warn('Message queue full, removing oldest message');
      this.queue.shift();
    }

    this.queue.push(queuedMessage);
    this.saveToStorage();
    
    console.log(`[MessageQueue] Message queued:`, queuedMessage);
    return queuedMessage.id;
  }

  /**
   * Process queue when connection is restored
   */
  async processQueue(ws, onSuccess, onError) {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.log('[MessageQueue] WebSocket not ready, skipping queue processing');
      return;
    }

    this.isProcessing = true;
    console.log(`[MessageQueue] Processing ${this.queue.length} queued messages`);

    while (this.queue.length > 0) {
      const message = this.queue[0];

      // Check if message is too old (>5 minutes)
      if (Date.now() - message.queuedAt > 300000) {
        console.warn('[MessageQueue] Message expired, removing:', message);
        this.queue.shift();
        this.saveToStorage();
        if (onError) onError(message, 'Message expired');
        continue;
      }

      // Check retry limit
      if (message.retries >= this.maxRetries) {
        console.error('[MessageQueue] Max retries reached, removing:', message);
        this.queue.shift();
        this.saveToStorage();
        if (onError) onError(message, 'Max retries exceeded');
        continue;
      }

      try {
        message.status = 'sending';
        message.retries++;

        // Send message
        console.log(`[MessageQueue] Sending message (attempt ${message.retries}):`, message);
        ws.send(JSON.stringify(message));

        // Remove from queue on success
        this.queue.shift();
        message.status = 'sent';
        this.saveToStorage();
        
        if (onSuccess) onSuccess(message);
        console.log('[MessageQueue] Message sent successfully:', message.id);

        // Small delay between messages
        await this.delay(100);

      } catch (error) {
        console.error('[MessageQueue] Failed to send message:', error);
        message.status = 'failed';

        // Exponential backoff
        const backoffDelay = this.retryDelay * Math.pow(2, message.retries - 1);
        console.log(`[MessageQueue] Retrying in ${backoffDelay}ms...`);
        await this.delay(backoffDelay);

        // If still failing after delay, check connection again
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          console.log('[MessageQueue] WebSocket disconnected, pausing queue processing');
          this.isProcessing = false;
          return;
        }
      }
    }

    this.isProcessing = false;
    console.log('[MessageQueue] Queue processing complete');
  }

  /**
   * Get pending message count
   */
  getPendingCount() {
    return this.queue.filter(msg => msg.status === 'pending').length;
  }

  /**
   * Clear all messages
   */
  clear() {
    this.queue = [];
    this.saveToStorage();
    console.log('[MessageQueue] Queue cleared');
  }

  /**
   * Remove specific message
   */
  remove(messageId) {
    this.queue = this.queue.filter(msg => msg.id !== messageId);
    this.saveToStorage();
  }

  /**
   * Save queue to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem('chatMessageQueue', JSON.stringify(this.queue));
    } catch (error) {
      console.error('[MessageQueue] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load queue from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('chatMessageQueue');
      if (stored) {
        this.queue = JSON.parse(stored);
        console.log(`[MessageQueue] Loaded ${this.queue.length} messages from storage`);
      }
    } catch (error) {
      console.error('[MessageQueue] Failed to load from localStorage:', error);
      this.queue = [];
    }
  }

  /**
   * Utility delay function
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Create singleton instance
const messageQueue = new MessageQueue();

export default messageQueue;
